import {RichEditor} from '@frontapp/editor-html';
import React, {FC, ReactNode, Ref, RefCallback, RefObject, useCallback, useEffect, useMemo} from 'react';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';

import {useConstant} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {RepositionPopover} from '../../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {
  FormattingPopoverAnchor,
  FormattingPopoverAnchorTypesEnum,
  useFormattingPopoverAnchor,
} from '../../../common/editor/formattingPopover/formattingPopoverAnchor';
import {useGrammarlyState} from '../../../grammarly/grammarlyContext';
import {CommentComposerFormattingLinkToolbar} from './commentComposerFormattingLinkToolbar';
import {CommentComposerFormattingToolbar} from './commentComposerFormattingToolbar';

/*
 * Props.
 */

interface CommentComposerFormattingPopoverProps {
  conversationId: number | undefined;
  editor: RichEditor;
  contentEditableRef: RefObject<HTMLElement>;
}

/*
 * Component.
 */

export const CommentComposerFormattingPopover: FC<CommentComposerFormattingPopoverProps> = (props) => {
  const {focus: focusService, selection: selectionService} = props.editor.services;
  const contentEditable$ = useObservableFromRef(props.contentEditableRef);
  const [contentWrapperRef, contentWrapper$] = useObservableRef<HTMLDivElement>();

  const [anchor, dismissAnchor] = useFormattingPopoverAnchor(
    selectionService,
    contentEditable$,
    contentWrapper$,
  );

  const {setShouldGrammarlySuggestionOpen} = useGrammarlyState();
  useEffect(() => {
    setShouldGrammarlySuggestionOpen(!anchor);
  }, [anchor, setShouldGrammarlySuggestionOpen]);

  const onRequestClose = useCallback(() => {
    dismissAnchor();
    focusService.focus();
  }, [dismissAnchor, focusService]);

  if (!anchor) {
    return null;
  }

  return (
    <RepositionPopover anchor={() => anchor.bounds} placement="top">
      <div ref={contentWrapperRef}>{renderContent(props, anchor, onRequestClose)}</div>
    </RepositionPopover>
  );
};

function renderContent(
  props: CommentComposerFormattingPopoverProps,
  anchor: FormattingPopoverAnchor,
  onRequestClose: () => void,
): ReactNode {
  switch (anchor.type) {
    case FormattingPopoverAnchorTypesEnum.LINK:
      return (
        <CommentComposerFormattingLinkToolbar
          conversationId={props.conversationId}
          linkElement={anchor.linkElement}
          onRequestClose={onRequestClose}
        />
      );

    case FormattingPopoverAnchorTypesEnum.SELECTION:
    default:
      return (
        <CommentComposerFormattingToolbar
          conversationId={props.conversationId}
          editor={props.editor}
          onRequestClose={onRequestClose}
        />
      );
  }
}

/*
 * Hooks.
 */

/** Returns an observable that emits an existing ref's value, when subscribed to and when it changes. */
function useObservableFromRef<T>(ref: RefObject<T>): Observable<T | null> {
  // Create an observable.
  const current$ = useConstant(() => new BehaviorSubject<T | null>(ref.current));

  // Feed the ref value into the observable after each render (in the effect phase, not layout phase).
  useEffect(() => current$.next(ref.current));

  // Return a de-duped version of the observable.
  return useMemo(() => current$.pipe(distinctUntilChanged()), [current$]);
}

/** Creates a new ref and observable pair. The observable emits the ref's value when subscribed to and when it changes. */
function useObservableRef<T>(): [Ref<T>, Observable<T | null>] {
  // Create an observable.
  const current$ = useConstant(() => new BehaviorSubject<T | null>(null));

  // Create a callback ref that feeds into the observable.
  const ref = useCallback<RefCallback<T>>((current) => current$.next(current), [current$]);

  return [ref, current$];
}
